import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Backdrop, Box, ClickAwayListener } from "@mui/material";
import { useQueryClient } from "react-query";

import CustomTooltip from "../../Components/Others/tooltip";

import renderCashOutUsingPaymentEntity from "../Modals/Funding Instruments/cash-out-using-payment-entity";

import { useModalContext } from "../../Contexts/ModalContext";

import { useActivities, useAppEssentials, usePrograms, useStakeDebit } from "../../CustomHooks/Queries";
import { useUpdateProgramPreviewStatus } from "../../CustomHooks/Mutations";

import { formatCurrency, isSVDApproved } from "../../Utils/helpers";
import { APIQueryKeys } from "../../Utils/constants";

import { CrossMark, PersonOutline } from "../../Assets/Icons";

export default function ProfileBar() {
  const navigate = useNavigate();

  const [isProgramsTooltipOpen, setIsProgramsTooltipOpen] = useState(false);

  const { isLoading: isLoadingActivities, data: activitiesData } = useActivities();

  const modalContext = useModalContext();
  const { modalVisibility } = modalContext;

  const queryClient = useQueryClient();

  const { data: stakeDebit } = useStakeDebit();

  const { data: appEssentialsData } = useAppEssentials();
  const { data: programs } = usePrograms({
    enabled: appEssentialsData?.propertyHasProgram ?? false,
  });

  const { mutate: UpdateProgramPreviewStatus } = useUpdateProgramPreviewStatus({
    onSuccess: () => {
      queryClient.refetchQueries(APIQueryKeys.appEssentials);
    },
  });

  const IsSVDApproved = useMemo(() => {
    return isSVDApproved(stakeDebit?.data);
  }, [stakeDebit?.data]);

  const walletAmount = useMemo(() => {
    if (isLoadingActivities) return 0;
    return activitiesData?.data?.data?.currentBalance ?? 0;
  }, [isLoadingActivities, activitiesData]);

  useEffect(() => {
    if (!!programs?.my_program && appEssentialsData?.previewProgramOnLogin && !modalVisibility) {
      setIsProgramsTooltipOpen(true);
    }
  }, [programs?.my_program, appEssentialsData?.previewProgramOnLogin, modalVisibility]);

  const handlePopperClose = () => {
    if (isProgramsTooltipOpen) {
      UpdateProgramPreviewStatus();
      setIsProgramsTooltipOpen(false);
    }
  };

  const handleProfileClick = () => {
    handlePopperClose();
    navigate("/profile");
  };

  return (
    <div className="profile-bar">
      {!IsSVDApproved && walletAmount > 0 && (
        <button className="wallet-info" onClick={renderCashOutUsingPaymentEntity.bind(this, modalContext)}>
          Wallet: {formatCurrency(walletAmount)}
        </button>
      )}
      {isProgramsTooltipOpen && (
        <Backdrop
          open={isProgramsTooltipOpen}
          sx={{
            zIndex: (theme) => theme.zIndex.tooltip - 1,
            background: "var(--backdrop-bg)",
            backdropFilter: "var(--backdrop-filter)",
          }}
        />
      )}
      <CustomTooltip
        className="program-popper-tooltip"
        title={
          <ClickAwayListener onClickAway={handlePopperClose}>
            <>
              <div className="program-popper-logo-section">
                <img
                  src={programs?.my_program?.image_src?.primary}
                  alt={programs?.my_program?.name}
                  className="program-logo"
                />
                <button className="close-button" onClick={handlePopperClose}>
                  <CrossMark />
                </button>
              </div>
              <div className="program-popper-content">
                <h4 className="program-title">{programs?.my_program?.name}</h4>
                <p className="program-description">
                  View your {programs?.my_program?.name} benefits inside your profile.
                </p>
              </div>
            </>
          </ClickAwayListener>
        }
        open={isProgramsTooltipOpen}
        placement="bottom-end"
      >
        <Box
          sx={{
            position: "relative",
            zIndex: isProgramsTooltipOpen ? (theme) => theme.zIndex.tooltip + 1 : "auto",
          }}
        >
          <button className="profile-button" onClick={handleProfileClick}>
            <PersonOutline />
          </button>
        </Box>
      </CustomTooltip>
    </div>
  );
}
